"use client";

import { useState } from "react";
import { useSearchParams } from "next/navigation";
import FilterChipGroup from "@/components/shared/FilterChipGroup";
import {
  DEFAULT_RELEASE_NOTE_AUDIENCE,
  isReleaseNoteAudience,
  RELEASE_NOTE_AUDIENCES,
  RELEASE_NOTE_AUDIENCE_LABELS,
  RELEASE_NOTE_AUDIENCE_PARAM,
  type ReleaseNote,
  type ReleaseNoteAudience,
} from "@/lib/releaseNoteAudiences";

const FILTER_OPTIONS: ReadonlyArray<{ value: ReleaseNoteAudience; label: string }> = RELEASE_NOTE_AUDIENCES.map(
  (audience) => ({ value: audience, label: RELEASE_NOTE_AUDIENCE_LABELS[audience] })
);

// 原本の日付（YYYY-MM-DD）をそのまま読み替える。Date を通すと表示する環境の時差で日がずれる。
function formatPublishedOn(publishedOn: string): string {
  const [year, month, day] = publishedOn.split("-").map(Number);
  return `${year}年${month}月${day}日`;
}

interface Props {
  notes: ReleaseNote[];
  /** 最初に選んでおく対象。開いた画面（企業・大学の管理画面、それ以外）で変わる */
  initialAudience: ReleaseNoteAudience;
}

// リリースノートの一覧（#2055）。対象（一般向け・企業向け・大学向け）を切り替えて表示し、
// 変更が残らなかったリリースは見出しごと隠す。
// 文面は PR 本文から来るため、HTML としては解釈せずテキストとして描画する。
export default function ReleaseNoteList({ notes, initialAudience }: Props) {
  const [audience, setAudience] = useState<ReleaseNoteAudience>(initialAudience);

  const visibleNotes = notes
    .map((note) => ({ ...note, items: note.items.filter((item) => item.audiences.includes(audience)) }))
    .filter((note) => note.items.length > 0);

  return (
    <div>
      <FilterChipGroup label="対象" options={FILTER_OPTIONS} value={audience} onChange={setAudience} />

      {visibleNotes.length === 0 ? (
        <p className="mt-8 text-sm text-subtext" data-testid="release_notes.empty">
          該当する変更はまだありません。
        </p>
      ) : (
        <div className="mt-6 space-y-10">
          {visibleNotes.map((note) => (
            <section key={note.version} data-testid={`release_notes.release.${note.version}`}>
              <h2 className="flex items-baseline gap-2 border-b border-border pb-2 text-lg font-bold text-text">
                {formatPublishedOn(note.publishedOn)}
                <span className="text-xs font-normal text-subtext">{note.version}</span>
              </h2>
              <ul className="mt-4 space-y-3">
                {/* 原本は手直しで同じ項目が重なりうるため、並び順を key に含めて重複させない */}
                {note.items.map((item, index) => (
                  <li
                    key={`${index}:${item.text}`}
                    className="flex flex-col gap-1.5 text-sm leading-relaxed text-text sm:flex-row sm:items-start sm:gap-3"
                  >
                    <span className="flex shrink-0 flex-wrap gap-1">
                      {item.audiences.map((itemAudience) => (
                        <span
                          key={itemAudience}
                          className="rounded-full border border-accent px-2 py-0.5 text-xs font-bold text-accent"
                        >
                          {RELEASE_NOTE_AUDIENCE_LABELS[itemAudience]}
                        </span>
                      ))}
                    </span>
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

// URL の ?for= を最初の選択にして一覧を出す。未指定・不正な値なら一般向けを選ぶ。
// ページは静的に生成するため、呼び出し側で Suspense に包む（読み込み前は一般向けを出す）。
// 同じページのまま ?for= だけが変わった場合（下部の案内のリンクなど）も選択を合わせるため、key で作り直す。
export function ReleaseNoteListFromQuery({ notes }: { notes: ReleaseNote[] }) {
  const value = useSearchParams().get(RELEASE_NOTE_AUDIENCE_PARAM);
  const initialAudience = isReleaseNoteAudience(value) ? value : DEFAULT_RELEASE_NOTE_AUDIENCE;
  return <ReleaseNoteList key={initialAudience} notes={notes} initialAudience={initialAudience} />;
}
